import React, { useState, useEffect } from 'react';
import { TextAreaComponent } from '@syncfusion/ej2-react-inputs';
import { DateTimePickerComponent } from '@syncfusion/ej2-react-calendars';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { type CrudFormProps } from '../../components/CrudPage';
import { type MaintenanceWindowDto, type ResourceDto, ResourceControllerService } from '../../api';
import { FormDialog } from '../../components/FormDialog';
import { type UseFormRegister, type FormState, type UseFormSetValue, type UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type MaintenanceWindowWithId = MaintenanceWindowDto & { id: string };

interface FormProps {
  register: UseFormRegister<MaintenanceWindowWithId>;
  formState: FormState<MaintenanceWindowWithId>;
  setValue: UseFormSetValue<MaintenanceWindowWithId>;
  watch: UseFormWatch<MaintenanceWindowWithId>;
}

const MaintenanceWindowFormDialog: React.FC<CrudFormProps<MaintenanceWindowWithId>> = ({
  value,
  onClose,
  onSave
}) => {
  const { t, i18n } = useTranslation();
  const [resources, setResources] = useState<ResourceDto[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch resources on component mount
  useEffect(() => {
    const fetchResources = async () => {
      try {
        setLoading(true);
        const resourcesData = await ResourceControllerService.findAll();
        setResources(resourcesData);
      } catch (error) {
        console.error('Error fetching resources:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchResources();
  }, []);

  return (
    <FormDialog
      value={value}
      onClose={onClose}
      onSave={onSave}
      titleKey="dialogs.maintenanceWindow"
    >
      {({ register, formState: { errors }, setValue, watch }: FormProps) => {
        const currentValue = watch();
        
        return (
          <>
            {/* Maintenance Type - Fixed to "SCHEDULED" */}
            <div className="mb-4">
              <input
                type="hidden"
                {...register('maintenanceType')}
                value="SCHEDULED"
              />
              <div className="text-gray-600 text-sm">
                {t('maintenanceWindow.form.maintenanceType')}: SCHEDULED
              </div>
            </div>

            {/* Resource Selection - Required */}
            <div className="mb-4">
              <DropDownListComponent
                dataSource={resources}
                fields={{ text: 'name', value: 'uuid' }}
                placeholder={`${t('maintenanceWindow.form.resourcePlaceholder')} *`}
                floatLabelType="Never"
                cssClass={errors.resourceId ? 'e-error' : ''}
                value={currentValue.resourceId}
                onChange={(e: any) => setValue('resourceId', e.value)}
                enabled={!loading}
              />
              {errors.resourceId && (
                <div className="text-red-500 text-sm mt-1">{errors.resourceId.message}</div>
              )}
            </div>

            {/* Start Date & Time */}
            <div className="mb-4">
              <DateTimePickerComponent
                {...register('maintenanceStart', { required: true })}
                placeholder={`${t('maintenanceWindow.form.startDatePlaceholder')} *`}
                format={i18n.language === 'de' ? 'dd.MM.yyyy HH:mm' : 'MM/dd/yyyy hh:mm a'}
                floatLabelType="Never"
                cssClass={errors.maintenanceStart ? 'e-error' : ''}
              />
              {errors.maintenanceStart && (
                <div className="text-red-500 text-sm mt-1">{errors.maintenanceStart.message}</div>
              )}
            </div>

            {/* End Date & Time */}
            <div className="mb-4">
              <DateTimePickerComponent
                {...register('maintenanceEnd', { required: true })}
                placeholder={`${t('maintenanceWindow.form.endDatePlaceholder')} *`}
                format={i18n.language === 'de' ? 'dd.MM.yyyy HH:mm' : 'MM/dd/yyyy hh:mm a'}
                floatLabelType="Never"
                cssClass={errors.maintenanceEnd ? 'e-error' : ''}
              />
              {errors.maintenanceEnd && (
                <div className="text-red-500 text-sm mt-1">{errors.maintenanceEnd.message}</div>
              )}
            </div>

            {/* Maintenance Description */}
            <div className="mb-4">
              <TextAreaComponent
                {...register('maintenanceDescription')}
                placeholder={t('maintenanceWindow.form.maintenanceDescriptionPlaceholder')}
                rows={3}
                floatLabelType="Never"
                cssClass={errors.maintenanceDescription ? 'e-error' : ''}
              />
              {errors.maintenanceDescription && (
                <div className="text-red-500 text-sm mt-1">{errors.maintenanceDescription.message}</div>
              )}
            </div>
          </>
        );
      }}
    </FormDialog>
  );
};

export default MaintenanceWindowFormDialog;
